import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { Switch } from "@capacit-com/lara-law-shared-ui";
import {
  ArrowLeft,
  Plus,
  X,
  Power,
  PowerOff,
  Trash2,
  MoreVertical,
  Pencil,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import type { SynonymGroup } from "@/models/synonym-group/synonymGroup";
import PageContent from "@/pages/PageContent";
import AppRoutes from "@/constants/routes/AppRoutes";
import RenameSynonymGroupDialog from "./components/RenameSynonymGroupDialog";
import useSynonymGroups from "@/hooks/useSynonymGroups";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";

const SynonymGroupDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    activate,
    deactivate,
    remove,
    addSynonym,
    removeSynonym,
    renameCanonicalTerm,
    useQuerySingle,
  } = useSynonymGroups();
  const { synonymGroupId } = useParams<{ synonymGroupId: string }>();
  if (!synonymGroupId!) navigate(AppRoutes.settingsSynonyms, { replace: true });

  const [group, setGroup] = useState<SynonymGroup | undefined>();
  const [newSynonym, setNewSynonym] = useState("");
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [isRemovingSynonym, setIsRemovingSynonym] = useState<
    string | undefined
  >(undefined);
  const fetchedGroup = useQuerySingle(synonymGroupId);

  useEffect(() => {
    if ((fetchedGroup.data, fetchedGroup.isFetched)) {
      setGroup(fetchedGroup.data);
    }
  }, [fetchedGroup.data, fetchedGroup.isFetched]);

  const activeSynonyms = useMemo(
    () => (group ? group.synonyms.filter((s) => s.active).length : 0),
    [group]
  );

  if (fetchedGroup.isLoading) {
    return (
      <PageContent
        title="Indlæser..."
        parent={{ title: "Synonym grupper", url: AppRoutes.settingsSynonyms }}
      >
        <Empty>
          <EmptyHeader>
            <EmptyMedia>
              <Spinner className="h-12 w-12" />
            </EmptyMedia>
            <EmptyTitle>Indlæser synonymgruppe...</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </PageContent>
    );
  }

  if (!group) return null;

  const onBack = () => navigate(AppRoutes.settingsSynonyms);

  const onToggleActive = async (g: SynonymGroup) => {
    try {
      const nextActive = !g.isActive;
      setGroup({ ...g, isActive: nextActive });
      if (nextActive) {
        await activate.mutateAsync({
          groupId: g.id,
          activateGroup: true,
          synonym: undefined,
        });
        showSuccessToast("Din synonym gruppe er aktiveret", "Succes");
      } else {
        await deactivate.mutateAsync({
          groupId: g.id,
          deactivateGroup: true,
          synonym: undefined,
        });
        showSuccessToast("Din synonym gruppe er deaktiveret", "Succes");
      }
    } catch (error) {
      logger.error("Failed to change synonym group active state", {
        operation: "toggleSynonymGroupActive",
        error,
        synonymGroupId: g.id,
        activate: !g.isActive,
      });
      setGroup((prev) => (prev ? { ...prev, isActive: !prev.isActive } : prev));
      showErrorToast("Kunne ikke opdatere gruppens status", "Fejl");
    }
  };

  const onDelete = async (g: SynonymGroup) => {
    try {
      await remove.mutateAsync(g.id);
      showSuccessToast("Gruppe slettet", "Succes");
      navigate(AppRoutes.settingsSynonyms);
    } catch (error) {
      logger.error("Failed to delete synonym group", {
        operation: "deleteSynonymGroup",
        error,
        synonymGroupId: g.id,
      });
      showErrorToast("Der skete en fejl under sletning af gruppe", "Fejl");
    }
  };

  const handleAddSynonym = async (groupId: string, original: string) => {
    try {
      await addSynonym.mutateAsync({
        groupId,
        synonym: original,
      });

      showSuccessToast("Synonym oprettet", "Succes");
    } catch (err) {
      logger.error("Failed to add synonym to synonym group", {
        operation: "addSynonym",
        error: err,
        synonymGroupId: groupId,
      });

      let msg = "";
      if (typeof err === "string") {
        msg = err;
      } else if (err instanceof Error) {
        msg = err.message;
      } else if (err && typeof err === "object" && "Message" in err) {
        msg = String((err as unknown as { Message: string }).Message);
      }

      if (msg?.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(msg);
          if (parsed?.Message) msg = String(parsed.Message);
          else if (parsed?.message) msg = String(parsed.message);
          else if (parsed?.title) msg = String(parsed.title);
        } catch {
          // ignore JSON parse errors
        }
      }

      if (
        msg
          .toLowerCase()
          .includes("synonym cannot be equal to the canonical term")
      ) {
        showErrorToast("Synonymet må ikke være en til en med termet", "Fejl");
      } else if (
        msg.toLowerCase().includes("synonym already exists in this group")
      ) {
        showErrorToast("Synonymet findes allerede på gruppen", "Fejl");
      } else {
        showErrorToast("Der skete en fejl under tilføjelse af synonym", "Fejl");
      }
    }
  };

  const handleRemoveSynonym = async (groupId: string, normalized: string) => {
    try {
      setIsRemovingSynonym(normalized);
      await removeSynonym.mutateAsync({
        groupId,
        synonym: normalized,
      });

      showSuccessToast("Synonym slettet", "Succes");
    } catch (error) {
      logger.error("Failed to remove synonym from synonym group", {
        operation: "removeSynonym",
        error,
        synonymGroupId: groupId,
      });
      showErrorToast("Der skete en fejl under sletning af synonym", "Fejl");
    } finally {
      setIsRemovingSynonym(undefined);
    }
  };

  const handleToggleSynonymActive = async (
    groupId: string,
    normalized: string
  ) => {
    let message = "";

    const target = group.synonyms.find((s) => s.normalized === normalized);
    if (!target) return;
    const next = !target.active;

    try {
      setGroup(
        (prev) =>
          prev && {
            ...prev,
            synonyms: prev.synonyms.map((s) =>
              s.normalized === normalized ? { ...s, active: next } : s
            ),
          }
      );
      if (next) {
        await activate.mutateAsync({
          groupId,
          activateGroup: false,
          synonym: normalized,
        });

        message = "Dit synonym er aktiveret";
      } else {
        await deactivate.mutateAsync({
          groupId,
          deactivateGroup: false,
          synonym: normalized,
        });

        message = "Dit synonym er deaktiveret";
      }

      showSuccessToast(message, "Succes");
    } catch (error) {
      setGroup(
        (prev) =>
          prev && {
            ...prev,
            synonyms: prev.synonyms.map((s) =>
              s.normalized === normalized ? { ...s, active: !s.active } : s
            ),
          }
      );
      logger.error("Failed to change synonym active state", {
        operation: "toggleSynonymActive",
        error,
        synonymGroupId: groupId,
        activate: next,
      });
      const message = target.active
        ? "Der skete en fejl under deaktivering af synonym"
        : "Der skete en fejl under aktivering af synonym";
      showErrorToast(message, "Fejl");
    }
  };

  const handleRename = async (groupId: string, newCanonTerm: string) => {
    try {
      await renameCanonicalTerm.mutateAsync({
        groupId,
        canonicalTerm: newCanonTerm,
      });

      showSuccessToast("Kanosisk term på gruppe opdateret");
    } catch (error) {
      logger.error("Failed to rename synonym group canonical term", {
        operation: "renameCanonicalTerm",
        error,
        synonymGroupId: groupId,
      });
      showErrorToast(
        "Det skete en fejl under opdatering af gruppens kanoniske term",
        "Fejl"
      );
    }
  };

  return (
    <PageContent
      title={group.canonicalTerm}
      parent={{ title: "Synonym grupper", url: AppRoutes.settingsSynonyms }}
    >
      <div className="flex flex-col space-y-2 p-4 xl:px-8">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tilbage til synonymgrupper
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-semibold text-foreground">
                  {group.canonicalTerm}
                </h1>
                <Badge
                  variant={group.isActive ? "default" : "secondary"}
                  className="text-xs"
                >
                  {group.isActive ? "Aktiv" : "Inaktiv"}
                </Badge>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {activeSynonyms} af {group.synonyms.length} synonymer aktive
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onToggleActive(group)}>
                  {group.isActive ? (
                    <>
                      <PowerOff className="mr-2 h-4 w-4" />
                      Deaktiver gruppe
                    </>
                  ) : (
                    <>
                      <Power className="mr-2 h-4 w-4" />
                      Aktiver gruppe
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(group)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Slet gruppe
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mb-8 rounded-lg border border-border bg-card p-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-medium text-muted-foreground">
              Kanonisk term
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRenameDialogOpen(true)}
              className="h-8 text-muted-foreground hover:text-foreground"
            >
              <Pencil className="mr-2 h-3 w-3" />
              Rediger
            </Button>
          </div>
          <p className="text-lg font-medium text-foreground">
            {group.canonicalTerm}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Alle aktive synonymer i denne gruppe vil blive mappet til denne
            kanoniske term i søgeresultater
          </p>
        </div>

        <div className="mb-6 rounded-lg border border-border bg-card p-6">
          <h2 className="mb-4 text-sm font-medium text-foreground">
            Tilføj synonym
          </h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (newSynonym.trim())
                handleAddSynonym(group.id, newSynonym.trim());
            }}
            className="flex gap-2"
          >
            <Input
              placeholder="Indtast et synonym..."
              value={newSynonym}
              onChange={(e) => setNewSynonym(e.target.value)}
              className="flex-1"
            />
            <Button type="submit">
              <Plus className="mr-2 h-4 w-4" />
              Tilføj
            </Button>
          </form>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-medium text-foreground">
              Synonymer ({group.synonyms.length})
            </h2>
          </div>
          {group.synonyms.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                Ingen synonymer tilføjet endnu. Tilføj dit første synonym
                ovenfor.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {group.synonyms.map((synonym) => (
                <div
                  key={synonym.normalized}
                  className="group flex items-center justify-between rounded-md border border-border bg-background px-4 py-3 transition-colors hover:bg-accent/50"
                >
                  <div className="flex flex-1 items-center gap-4">
                    <Switch
                      checked={synonym.active}
                      onCheckedChange={() =>
                        handleToggleSynonymActive(group.id, synonym.normalized)
                      }
                      className="cursor-pointer"
                      aria-label={
                        synonym.active ? "Deaktiver synonym" : "Aktiver synonym"
                      }
                    />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Original:
                        </span>
                        <span className="font-mono text-sm text-foreground">
                          {synonym.original}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Normaliseret:
                        </span>
                        <span className="font-mono text-sm text-muted-foreground">
                          {synonym.normalized}
                        </span>
                      </div>
                    </div>
                    <Badge
                      variant={synonym.active ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {synonym.active ? "Aktiv" : "Inaktiv"}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "ml-2 h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100",
                      isRemovingSynonym === synonym.normalized
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                    onClick={() =>
                      handleRemoveSynonym(group.id, synonym.normalized)
                    }
                  >
                    {isRemovingSynonym === synonym.normalized ? (
                      <Spinner className="size-4" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {group.createdAt && group.updatedAt && (
          <div className="mt-6 text-xs text-muted-foreground mb-2">
            <p>Oprettet: {new Date(group.createdAt).toLocaleString("da-DK")}</p>
            <p>
              Sidst opdateret:{" "}
              {new Date(group.updatedAt).toLocaleString("da-DK")}
            </p>
          </div>
        )}
      </div>
      <RenameSynonymGroupDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        currentCanonicalTerm={group.canonicalTerm}
        onRename={(term) => handleRename(group.id, term)}
      />
    </PageContent>
  );
};

export default SynonymGroupDetailPage;
